// required-label.directive.ts
import {
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs/operators';

@Directive({
  selector: '[appRequiredLabel]',
  standalone: true,
})
export class RequiredLabelDirective implements OnInit {
  @Input('appRequiredLabel') controlName!: string;

  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private formGroupDir = inject(FormGroupDirective, { optional: true });
  readonly #destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const control = this.formGroupDir?.form.get(this.controlName);
    const formGroup = this.formGroupDir?.form;
    if (!control || !formGroup) return;

    // Obsługa klasycznych walidatorów (statycznych)
    if (control.hasValidator(Validators.required)) {
      this.host.nativeElement.classList.add('required');
      return;
    }

    // Nasłuchuj ZMIAN STATUSU CAŁEGO FORMGROUP - łapie cross-field validation
    formGroup.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef), distinctUntilChanged())
      .subscribe(() => {
        const isRequired = this.#isCurrentlyRequired(control);
        this.#updateRequiredClass(isRequired);
      });

    // Inicjalne sprawdzenie
    const initialRequired = this.#isCurrentlyRequired(control);
    this.#updateRequiredClass(initialRequired);
  }

  #isCurrentlyRequired(control: AbstractControl): boolean {
    // Test 1: Sprawdź aktualne błędy kontrolki
    if (control.touched && control.errors?.['required']) {
      return true;
    }

    // Test 2: Symuluj pustą wartość w kontekście całego formularza
    const originalValue = control.value;

    try {
      control.setValue(null, { emitEvent: false });
      control.markAsTouched({ onlySelf: true });

      // Kluczowe: updateValueAndValidity BEZ onlySelf - propaguje do cross-field validators
      control.updateValueAndValidity({ emitEvent: false });

      return !!control.errors?.['required'];
    } finally {
      // Przywracaj stan
      control.setValue(originalValue, { emitEvent: false });
      control[originalValue ? 'markAsUntouched' : 'markAsTouched']({
        onlySelf: true,
      });
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  #updateRequiredClass(isRequired: boolean): void {
    const element = this.host.nativeElement;
    if (isRequired) {
      element.classList.add('required');
    } else {
      element.classList.remove('required');
    }
  }
}
